import { useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { Box, Button, Dialog, DialogContent, IconButton, Stack, Typography } from '@mui/material';

import { emailRegex } from 'constants/regex';
import { dialogPropsSelector } from 'store/entities/ui/slice';
import { useResetPasswordMutation } from 'store/entities/auth/api';
import { Input } from 'components/simple';
import { CrossIcon as CloseIcon, ResetPasswordIcon } from 'assets/icons';

const styles = {
  dialogPaper: {
    width: '100%',
    maxWidth: 540,
    borderRadius: '40px',
    margin: 1,
  },
  closeButton: ({ palette }) => ({
    position: 'absolute',
    top: 32,
    right: 20,
    color: palette.colors.darkGrey,
  }),
  dialogContent: ({ breakpoints }) => ({
    paddingBlock: 8,
    paddingInline: 2,
    [breakpoints.up('sm')]: {
      paddingInline: 5,
    },
  }),
  caption: {
    width: '100%',
    maxWidth: 380,
    color: 'rgba(0, 0, 0, 0.6)',
  },
  form: { width: '100%' },
  submitButton: { borderRadius: '100px' },
};

const defaultValues = {
  email: '',
};

export const ResetPasswordDialog = () => {
  const { onClose } = useSelector(dialogPropsSelector);
  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm({ defaultValues, mode: 'onChange' });

  const handleClose = () => {
    onClose();
  };

  const onSubmit = async (data) => {
    await resetPassword({
      email: data.email.toLowerCase(),
    });
    handleClose();
  };

  return (
    <Dialog
      fullWidth
      open
      onClose={isLoading ? undefined : handleClose}
      slotProps={{ paper: { sx: styles.dialogPaper } }}
    >
      <IconButton onClick={handleClose} sx={styles.closeButton} disabled={isLoading}>
        <CloseIcon />
      </IconButton>

      <DialogContent sx={styles.dialogContent}>
        <Stack spacing={{ xs: 2, sm: 3 }} alignItems="center">
          <Box mb={{ xs: 0, sm: 2 }}>
            <ResetPasswordIcon />
          </Box>

          <Typography variant="h2" align="center">
            Forgot your password?
          </Typography>

          <Typography variant="body1" align="center" sx={styles.caption}>
            No worries, we’ll send you reset instructions.
          </Typography>

          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={styles.form}>
            <Stack spacing={8}>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: 'Email is required',
                  pattern: { value: emailRegex, message: 'Current value is not a valid email' },
                }}
                render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <Input
                    {...field}
                    id="reset-password-email"
                    innerRef={ref}
                    label="Email"
                    placeholder="Enter your email"
                    error={error}
                  />
                )}
              />

              <Button fullWidth type="submit" disabled={!isValid} loading={isLoading} sx={styles.submitButton}>
                Reset password
              </Button>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
